/**
 * Searchable firm picker using shadcn Popover + Command.
 * Shows firms in "Name (ID)" format matching Apx.Core's HeaderFirmList pattern.
 */

import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useFirms } from '@/hooks/useFirms';

export interface FirmComboboxProps {
  value: number | null;
  onChange: (firmId: number) => void;
}

export function FirmCombobox({ value, onChange }: FirmComboboxProps) {
  const [open, setOpen] = useState(false);
  const { data: firms = [], isLoading } = useFirms({ enabled: true });

  const selectedFirm = firms.find((f) => f.firm_id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-[320px] justify-between"
        >
          {selectedFirm
            ? `${selectedFirm.name} (${selectedFirm.firm_id})`
            : isLoading
              ? 'Loading firms...'
              : 'Select firm...'}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0">
        <Command>
          <CommandInput placeholder="Search firms..." />
          <CommandList>
            <CommandEmpty>No firm found.</CommandEmpty>
            <CommandGroup>
              {firms.map((firm) => (
                <CommandItem
                  key={firm.firm_id}
                  value={`${firm.name} (${firm.firm_id})`}
                  onSelect={() => {
                    onChange(firm.firm_id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === firm.firm_id ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  {firm.name} ({firm.firm_id})
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
